import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  BellOff,
  Building2,
  CalendarDays,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  ScanLine,
  Ticket,
  TrafficCone,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  ensureAdminPushSubscription,
  isPushSupported,
  subscribeAdminPush,
  unsubscribeAdminPush,
} from "@/lib/adminPush";

const linkBase =
  "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors";

function SidebarLink({ to, icon: Icon, label, testid, end, badge }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `${linkBase} ${
          isActive
            ? "bg-gradient-to-r from-svj-orange/20 via-svj-pink/20 to-svj-purple/20 text-white border border-svj-border"
            : "text-slate-300 hover:text-white hover:bg-svj-bg/60"
        }`
      }
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{label}</span>
      {!!badge && (
        <span data-testid="admin-nav-incidents-badge" className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

// Sprint 09H-B — organizer Web Push opt-in, one device/browser at a time (§40). A
// small self-contained control so it can be dropped into both the desktop sidebar
// footer and the mobile menu without duplicating its state logic.
function PushToggle() {
  const { authAxios } = useAuth();
  const [supported] = useState(isPushSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!supported) {
      setChecked(true);
      return;
    }
    // Production fix (§10/§11): also self-heals a subscription left over from
    // before a VAPID keypair rotation — needs no new permission prompt since
    // Notification.permission is already "granted" for an existing subscription.
    ensureAdminPushSubscription(authAxios)
      .then((result) => setSubscribed(result.state === "ok" || result.state === "resynced"))
      .catch(() => setSubscribed(false))
      .finally(() => setChecked(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  if (!supported) {
    return <div className="text-[11px] text-slate-500">Pushmeldingen niet ondersteund in deze browser</div>;
  }
  if (!checked) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await unsubscribeAdminPush(authAxios);
        setSubscribed(false);
        toast.success("Pushmeldingen uitgeschakeld op dit apparaat");
      } else {
        await subscribeAdminPush(authAxios);
        setSubscribed(true);
        toast.success("Pushmeldingen ingeschakeld op dit apparaat");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Kon pushmeldingen niet wijzigen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid="admin-push-toggle"
      className="w-full inline-flex items-center gap-2 text-xs text-slate-300 hover:text-white disabled:opacity-50"
    >
      {subscribed ? <Bell className="h-3.5 w-3.5 text-emerald-400" /> : <BellOff className="h-3.5 w-3.5" />}
      {subscribed ? "Incidentmeldingen actief" : "Incidentmeldingen inschakelen"}
    </button>
  );
}

export default function AdminLayout() {
  const { me, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openIncidentCount, setOpenIncidentCount] = useState(0);
  const isSuper = !!me?.is_superadmin;
  const isScanner = me?.role === "scanner";
  const isEventUser = me?.role === "event_user";
  // "verkeersbeheerder" -- event-scoped exactly like event_user (see
  // require_traffic_access/_check_event_scope in backend/server.py), but confined
  // to StapKompas Verkeer only: Verkeersregelaars/Live Flow/Incidenten/Chat, never
  // full event management. Gets its own restricted nav below rather than reusing
  // the full event_user one.
  const isTrafficCoordinator = me?.role === "traffic_coordinator";
  // An event_user's single event has nothing meaningful to show on Incidenten
  // when StapKompas isn't active for it (incident reporting is a StapKompas
  // sub-setting). An org_admin/org_owner previously always saw these links
  // regardless of whether ANY of their organisation's events actually use
  // StapKompas/multi-day registration -- reported directly from production
  // (an org with a single, ordinary event still showed Incidenten/Live Flow/
  // Presentie). Fixed by fetching that organisation's own event list once and
  // checking it the same way an event_user's single event already was.
  // Superadmin manages the whole platform across every organisation, so keeps
  // seeing every link unconditionally, same as before.
  const [orgEvents, setOrgEvents] = useState(null);
  useEffect(() => {
    if (isScanner || isEventUser || isTrafficCoordinator || isSuper) return;
    let cancelled = false;
    authAxios
      .get("/admin/events")
      .then(({ data }) => { if (!cancelled) setOrgEvents(data.items || []); })
      .catch(() => { if (!cancelled) setOrgEvents([]); });
    return () => { cancelled = true; };
  }, [authAxios, isScanner, isEventUser, isTrafficCoordinator, isSuper]);
  const orgHasCompanion = !!orgEvents?.some((ev) => ev.companion_app_enabled);
  const orgHasMultiDayRegistration = !!orgEvents?.some((ev) => ev.multi_day_registration_enabled);
  const orgHasTimeslotBooking = !!orgEvents?.some((ev) => ev.timeslot_booking_enabled);
  const orgHasTrafficControl = !!orgEvents?.some((ev) => ev.traffic_control_enabled);
  const showIncidentsPage = isSuper
    ? true
    : isEventUser
      ? !!me?.event?.companion_app_enabled
      : orgHasCompanion;
  const showPresencePage = isSuper
    ? true
    : isEventUser
      ? !!me?.event?.companion_app_enabled || !!me?.event?.multi_day_registration_enabled || !!me?.event?.timeslot_booking_enabled
      : orgHasCompanion || orgHasMultiDayRegistration || orgHasTimeslotBooking;
  // Live Flow is a StapKompas sub-setting like Incidenten -- same gating.
  const showLiveFlowPage = isSuper
    ? true
    : isEventUser
      ? !!me?.event?.companion_app_enabled
      : orgHasCompanion;
  // StapKompas Verkeer is its own independent flag (not a Companion sub-setting) --
  // see docs/companion-app/ARCHITECTURE.md.
  const showTrafficControlPage = isSuper
    ? true
    : isEventUser
      ? !!me?.event?.traffic_control_enabled
      : orgHasTrafficControl;

  const onLogout = async () => {
    await logout();
    toast.success("Uitgelogd");
    navigate("/");
  };

  // Sprint 09H-B §25 — sidebar open-incident badge, polled the same way
  // CompanionPresence already polls elsewhere in this admin app (Realtime on the
  // Incident Center pages themselves keeps that view live; this badge is a light
  // ambient indicator visible from anywhere in the admin).
  useEffect(() => {
    if (isScanner) return undefined;
    let cancelled = false;
    const refresh = () => {
      authAxios
        .get("/admin/incidents/summary")
        .then(({ data }) => { if (!cancelled) setOpenIncidentCount(data.open_count || 0); })
        .catch(() => {});
    };
    refresh();
    const timer = setInterval(refresh, 20000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [authAxios, isScanner]);

  // Sprint 09H-B §69 — admin-scoped manifest, linked only while the admin shell is
  // mounted (never on the public ticket shop's own pages, which share this SPA's one
  // index.html but must never be offered this install identity).
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/admin-manifest.json";
    document.head.appendChild(link);
    if (isPushSupported()) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // traffic_coordinator gets its own, deliberately minimal nav -- only the four
  // capabilities this role actually has (see require_traffic_access in
  // backend/server.py): Verkeersregelaars, Live Flow, Incidenten, Chat. Never
  // Dashboard/Events/Presentie/Rapportage/Scannen/Gebruikers, which stay full
  // event-management surfaces this role was deliberately never given.
  const nav = isTrafficCoordinator ? (
    <nav className="flex flex-col gap-1" data-testid="admin-nav">
      <SidebarLink to="/admin/traffic-control" icon={TrafficCone} label="Verkeersregelaars" testid="admin-nav-traffic-control" />
      <SidebarLink to="/admin/live-flow" icon={Activity} label="Live Flow" testid="admin-nav-live-flow" />
      <SidebarLink to="/admin/incidents" icon={AlertTriangle} label="Incidenten" testid="admin-nav-incidents" badge={openIncidentCount} />
      <SidebarLink to="/admin/traffic-chat" icon={MessageSquare} label="Chat" testid="admin-nav-traffic-chat" />
    </nav>
  ) : (
    <nav className="flex flex-col gap-1" data-testid="admin-nav">
      {!isScanner && (
        <SidebarLink to="/admin" end icon={LayoutDashboard} label="Dashboard" testid="admin-nav-dashboard" />
      )}
      {!isScanner && (
        <SidebarLink to="/admin/events" icon={CalendarDays} label="Events" testid="admin-nav-events" />
      )}
      <SidebarLink to="/admin/scan" icon={ScanLine} label="Scannen" testid="admin-nav-scan" />
      {!isScanner && showPresencePage && (
        <SidebarLink to="/admin/presence" icon={UserCheck} label="Presentie" testid="admin-nav-presence" />
      )}
      {!isScanner && (
        <SidebarLink to="/admin/reports" icon={BarChart3} label="Rapportage" testid="admin-nav-reports" />
      )}
      {!isScanner && showIncidentsPage && (
        <SidebarLink to="/admin/incidents" icon={AlertTriangle} label="Incidenten" testid="admin-nav-incidents" badge={openIncidentCount} />
      )}
      {!isScanner && showLiveFlowPage && (
        <SidebarLink to="/admin/live-flow" icon={Activity} label="Live Flow" testid="admin-nav-live-flow" />
      )}
      {!isScanner && showTrafficControlPage && (
        <SidebarLink to="/admin/traffic-control" icon={TrafficCone} label="Verkeersregelaars" testid="admin-nav-traffic-control" />
      )}
      {isSuper && (
        <SidebarLink to="/admin/organizations" icon={Building2} label="Organisaties" testid="admin-nav-orgs" />
      )}
      {(isSuper || isEventUser) && (
        <SidebarLink to="/admin/users" icon={Users} label="Gebruikers" testid="admin-nav-users" />
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-svj-bg" data-testid="admin-layout">
      <div className="flex">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-svj-card border-r border-svj-border flex-col z-40">
          <div className="p-6 border-b border-svj-border">
            <Link to="/" className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-svj-orange via-svj-pink to-svj-purple">
                <Ticket className="h-5 w-5 text-white" />
              </span>
              <span className="text-white font-extrabold tracking-tight text-base">
                StudioVoorJou<span className="text-svj-cyan"> Beheer</span>
              </span>
            </Link>
          </div>
          <div className="flex-1 p-4 overflow-y-auto">{nav}</div>
          <div className="p-4 border-t border-svj-border">
            <div className="text-xs text-slate-400 mb-2">
              {me?.first_name} {me?.last_name}
            </div>
            <div className="text-xs text-slate-500 truncate">{me?.email}</div>
            <div className="mt-2 inline-flex rounded-full bg-svj-bg border border-svj-border text-slate-300 px-2 py-0.5 text-[10px] uppercase tracking-widest">
              {me?.role}
              {me?.organization?.name && ` · ${me.organization.name}`}
            </div>
            {!isScanner && (
              <div className="mt-3">
                <PushToggle />
              </div>
            )}
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout"
              className="mt-4 w-full inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
            >
              <LogOut className="h-4 w-4" /> Uitloggen
            </button>
          </div>
        </aside>

        {/* Mobile header */}
        <div className="lg:hidden fixed inset-x-0 top-0 z-40 bg-svj-card border-b border-svj-border h-14 px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-svj-orange via-svj-pink to-svj-purple">
              <Ticket className="h-4 w-4 text-white" />
            </span>
            <span className="text-white font-bold text-sm">Beheer</span>
          </Link>
          <button
            type="button"
            data-testid="admin-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-white p-2"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden fixed inset-x-0 top-14 z-40 bg-svj-card border-b border-svj-border p-4" data-testid="admin-mobile-menu">
            <div onClick={() => setOpen(false)}>{nav}</div>
            {!isScanner && (
              <div className="mt-3 pt-3 border-t border-svj-border">
                <PushToggle />
              </div>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="mt-4 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
            >
              <LogOut className="h-4 w-4" /> Uitloggen
            </button>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 lg:pl-64 pt-14 lg:pt-0 min-w-0">
          <div className="p-6 md:p-8 max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
